import * as THREE from 'three';

import AbstractObject from '../misc/object';

import textureImg from '../../assets/textures/steel_1.jpg';
import Arrow from './arrow';
import { HTMLEditableLabel, HTMLLabel } from './html-label';
import Line from './line';

import Animate from '../services/animate';

import {
  toRadians,
  triangleGeometry,
  moveObjectPivotToEdge,
  generatePlanarUVMap,
  unitTransform,
  addDistanceLabel,
  prepareNum,
  sortPolygonPoints,
  getCenterOfPoints,
  getEndpoints
  //getEndpoints
} from '../../services/helper.service';

import Scene from '@scene';

import { isConvex } from '../services/helper';
import { defaultUnits } from '@config';
import { LabelsPadding } from './ground';
import { DISTANCE } from '@reducers/units';
import { APP_TEXTS } from '@texts';
import Layer from '@scene/misc/layer';
import DraggableObject from './draggable-object';

const defaultTexture = new THREE.TextureLoader().load(textureImg);

/* eslint-disable */
export default class Panel extends AbstractObject {

  static types = {
    SQUARE: 'square',
    CIRCLE: 'circle',
    TRIANGLE: 'triangle',
    BEND: 'bend',
    CUSTOM: 'custom'
  };

  static positions = {
    VERTICAL_X: 'vertical-x',
    VERTICAL_X_INVERT: 'vertical-x-invert',
    VERTICAL_Y: 'vertical-y',
    VERTICAL_Y_INVERT: 'vertical-y-invert',
    HORIZONTAL_X: 'horizontal-x',
    HORIZONTAL_Y: 'horizontal-y'
  };

  constructor(options, texture) {
    super(options);

    this.texture = texture || defaultTexture;

    // TODO: Tmp decision
    if (this.options.type !== Panel.types.BEND) {
      const tensionMaterial = new THREE.MeshLambertMaterial({
        color: this.options.color || 0xffffff,
        side: THREE.DoubleSide,
        map: this.texture
      });

      if (this.options.compressionOptions && this.options.compressionOptions.hasCompressionZone){
        const compressionMaterial = new THREE.MeshLambertMaterial({
          color: 0xF6C817,
          side: THREE.DoubleSide,
          map: this.texture
        });

        const object = new THREE.Object3D();
        var tensionZone = this.options.compressionOptions.tensionZone;
        var compressionZone = this.options.compressionOptions.compressionZone;
        if (this.options.type === Panel.types.TRIANGLE) {
          const geometry = this.getGeometry({ // create geometry just to get the center of the triangle
            width: this.size.width,
            height: this.size.height,
            type: this.options.type,
            depth: this.size.depth
          });

          object.position.y = this.center.x;
          object.position.z = -this.center.y;
          tensionZone = tensionZone.map((coord) => { return { X: coord.X + this.center.x, Y: coord.Y -this.center.y };});
          compressionZone = compressionZone.map((coord) => { return { X: coord.X + this.center.x, Y: coord.Y -this.center.y };});
        } 

        const tensionGeometry = this.getGeometryFromPoints(tensionZone, this.size.depth);
        const tensionMesh = new THREE.Mesh(tensionGeometry, tensionMaterial);
        tensionMesh.frustumCulled = false;
        tensionMesh.name = "TensionZone";
        object.add(tensionMesh);

        const compressionGeometry = this.getGeometryFromPoints(compressionZone, this.size.depth);
        const compressionMesh = new THREE.Mesh(compressionGeometry, compressionMaterial);
        compressionMesh.frustumCulled = false;
        compressionMesh.name = "CompressionZone";
        object.add(compressionMesh);

        object.rotation.z -= Math.PI / 2;
        object.rotation.y = Math.PI / 2;
        object.position.x -= this.size.depth / 2;

        this.panel = object;
        
      } else {
        const geometry = this.getGeometry({
          width: this.size.width,
          height: this.size.height,
          type: this.options.type,
          depth: this.size.depth
        });
        const panel = new THREE.Mesh(geometry, tensionMaterial);

        this.panel = panel;
        if (this.options.type === Panel.types.TRIANGLE) {
          this.panel.position.y = this.center.x;
          this.panel.position.z = -this.center.y;
        } 
        this.prepareMesh(this.options.type);
      }
    } else if (this.options.type === Panel.types.BEND) {
      this.panel = this.getBendPanel();
    }
    // if the panel is not rendered on the 2D view before resetting the camera, all the dimension lines will be hidden behind the panel
    // always render the panel to prevent hiding stuff on the z-buffer
    this.panel.frustumCulled = false; 

    this.updatePosition();
    
    this.object.addMesh(this.panel);

    // Generate screws for panel
    // if (this.options.screw && this.options.screw.custom) {
    //   if (this.options.screw.custom.active && this.options.screw.custom.positions.length) {
    //     // this._screwCoortinates = this.options.screw.custom.positions;
    //     this._screwCoortinates = this.options.screw.custom.positions.map((screw) => {
    //       return {
    //         x: screw.x,
    //         y: screw.y * (-1),
    //         z: screw.z * (-1)
    //       };
    //     });
    //   } else {
    //     this._screwCoortinates = this.getScrewsCoords();
    //   }

    //   if (this.options.type !== Panel.types.BEND) {
    //     this.generateScrews(this._screwCoortinates, this.options.screw);
    //   } else if (this.options.type === Panel.types.BEND) {
    //     this.generateScrewsForBend(this._screwCoortinates);
    //   }
    // }
  }

  get type () {
    return this.options.type || null;
  }

  get position() {
    return this.object.mesh.position;
  }

  get size() {
    return {
      width: this.options.width,
      height: this.options.height,
      depth: this.options.depth,
      topHeight: this.options.lyh && this.options.lyh.value ? this.options.lyh.value : this.options.topHeight
    };
  }

  get screwCoordinates() {
    return this._screwCoortinates;
  }

  get animate() {
    const context = this;

    return {
      get move() {
        return {
          x() { Animate.move(context.mesh, 'x', 1) },
          negativeX() { Animate.move(context.mesh, 'x', -1) },
          y() { Animate.move(context.mesh, 'y', -1) },
          negativeY() { Animate.move(context.mesh, 'y', 1) },
          z() { Animate.move(context.mesh, 'z', -1) },
          negativeZ() { Animate.move(context.mesh, 'z', 1) },
        };
      },
      get rotate() {
        return {
          x() { Animate.rotate(context.mesh, 'x') },
          negativeX() { Animate.rotate(context.mesh, 'x', true) },
          y() { Animate.rotate(moveObjectPivotToEdge(context.mesh, 'z', context.size.height * -1), 'y') },
          negativeY() { Animate.rotate(moveObjectPivotToEdge(context.mesh, 'z', context.size.height), 'y', true)},
          z() { Animate.rotate(moveObjectPivotToEdge(context.mesh, 'y', context.size.width), 'z') },
          negativeZ() { Animate.rotate(moveObjectPivotToEdge(context.mesh, 'y', context.size.width * -1), 'z', true) }
        };
      }
    };
  }

  getBendPanel() {
    const object = new THREE.Object3D();

    const geometry = this.getGeometry({
      width: this.size.width,
      height: this.size.height,
      type: this.options.type,
      depth: this.size.depth
    });
    const material = new THREE.MeshLambertMaterial({
      color: this.options.color || 0xffffff,
      side: THREE.DoubleSide,
      map: this.texture
    });

    const sidePanel = new THREE.Mesh(geometry, material);
    sidePanel.frustumCulled = false;
    sidePanel.rotation.z = Math.PI / 2;
    object.add(sidePanel);

    const topGeometry = this.getGeometry({
      width: this.size.width,
      height: this.size.topHeight - this.size.depth,
      type: this.options.type,
      depth: this.size.depth
    });
    const topPanel = new THREE.Mesh(topGeometry, material);
    topPanel.frustumCulled = false;
    topPanel.rotation.y = Math.PI / 2;
    topPanel.rotation.z = Math.PI / 2;

    topPanel.position.x = this.size.height / 2 - this.size.depth / 2;
    topPanel.position.z = this.size.topHeight / 2;
    object.add(topPanel);

    return object;
  }

  isObjectOnPanel(objects) {
    const createTriangleMesh = (indexA, indexB, indexC, positions, panel) => {
      const a = new THREE.Vector3(positions.getX(indexA), positions.getY(indexA), positions.getZ(indexA));
      const b = new THREE.Vector3(positions.getX(indexB), positions.getY(indexB), positions.getZ(indexB));
      const c = new THREE.Vector3(positions.getX(indexC), positions.getY(indexC), positions.getZ(indexC));

      panel.localToWorld(a);
      panel.localToWorld(b);
      panel.localToWorld(c);

      return new THREE.Triangle(a, b, c);
    };

    const outsidePanel = [];
    var vertices, indices;
    for (let objIdx = 0; objIdx < objects.length; objIdx++) {
      const item = objects[objIdx];
      if (this.panel.geometry && this.panel.geometry.attributes.position) {
        let isOnPanel = false;
        indices = this.panel.geometry.index;
        vertices = this.panel.geometry.attributes.position;
        if (indices === null) { // non-indexed geometry
          for (let i = 0; i <= vertices.count; i+=3) {
            if (createTriangleMesh(i, i+1, i+2, vertices, this.panel).intersectsBox(item.bbox)) {
              isOnPanel = true;
              break;
            }
          }

        } else { // indexed geometry
          for (let index = 0; index <= indices.count; index+=3) {
            if (createTriangleMesh(indices.array[index], indices.array[index+1], indices.array[index+2], vertices, this.panel).intersectsBox(item.bbox)) {
              isOnPanel = true;
              break;
            }
          }
        }

        if (!isOnPanel) {
          outsidePanel.push(item);
          item.setColor(0xff0000);
        }
      } else if (0 < this.panel.children.length) { // panel might be made up from tension & compression meshes or bend panels
        let isOnChildPanel = false;
        for (let childIdx = 0; childIdx < this.panel.children.length; childIdx++) {
          const child = this.panel.children[childIdx];
          if (!isOnChildPanel && child.geometry && child.name !== "CompressionZone") { // the screws should be red in compression zone
            indices = child.geometry.index;
            vertices = child.geometry.attributes.position;
            if (indices === null) { // non-indexed geometry
              for (let i = 0; i <= vertices.count; i+=3) {
                if (createTriangleMesh(i, i+1, i+2, vertices, child).intersectsBox(item.bbox)) {
                  isOnChildPanel = true;
                  break;
                }
              }
            } else { // indexed geometry
              for (let i = 0; i <= indices.count; i+=3) {
                if (createTriangleMesh(indices.array[i], indices.array[i+1], indices.array[i+2], vertices, child).intersectsBox(item.bbox)) {
                  isOnChildPanel = true;
                  break;
                }
              }
            }
          }
        }

        if (!isOnChildPanel) {
          outsidePanel.push(item);
          item.setColor(0xff0000);
        }
      }
    }

    return outsidePanel;
  }

  getGeometry({ width, height, depth, type }) {
    switch (type) {
      case Panel.types.SQUARE: {
        // VladP: This will override panel type in state
        //this.options.type = Panel.types.CUSTOM;
        this.options.vertices.count = 4;
        this.options.vertices.positions = [];

        return generatePlanarUVMap(
          this.getGeometry({
            width: this.size.width,
            height: this.size.height,
            type: Panel.types.CUSTOM,//this.options.type,
            depth: this.size.depth
          })
        );
      }
      case Panel.types.CIRCLE: {
        const radius = width / 2;
        const radiusSegments = 64; // 64 is enough for a perfect circle, prevent creating too many triangles on the mesh

        const { x, y, z } = this.position;
        setPanelVertices([{ x, y, z }], true);

        return new THREE.CylinderGeometry(radius, radius, depth, radiusSegments);
      }
      case Panel.types.TRIANGLE: {
        // height /= 2;
        // width /= 2;

        // let coords = [
        // {
        //   x: 0,
        //   y: 0,
        //   z: height
        // },
        // {
        //   x: 0,
        //   y: width,
        //   z: -height
        // },
        // {
        //   x: 0,
        //   y: -width,
        //   z: -height
        // }];
        var triangleArea = width * height / 2; // base * height
        var triangleSide = Math.sqrt(Math.pow(width / 2, 2) + Math.pow(height, 2));
        var triangleInnerCircleRadius = triangleArea / ((2 * triangleSide + width) / 2);

        let coords = [
        {
          x: -width/2,
          y: -triangleInnerCircleRadius,
          z: 0
        },
        {
          x: width/2,
          y: -triangleInnerCircleRadius,
          z: 0
        },
        {
          x: 0,
          y: height - triangleInnerCircleRadius,
          z: 0
        }];

        // VladP: Keep it the same as the service 
        var centerX = coords[0].x + 0.5 * (coords[1].x - coords[0].x);
        centerX = centerX + 0.5 * (coords[2].x - centerX);
        var centerY = coords[0].y + 0.5 * (coords[1].y - coords[0].y);
        centerY = centerY + 0.5 * (coords[2].y - centerY);
        this.center = new THREE.Vector3(centerX, centerY, 0);
       
        //.map((screw) => { return { x: screw.y * -1, y: screw.z }; })
        // setPanelVertices(coords.map((coord) => { return { x: 0, y: coord.x * -1, z: coord.y }}));
        // VladP: update current state, in order to create the vertices labels without modifying the coordinates
        this.options.vertices.positions = coords.map((coord) => { return { x: 0, y: coord.x * -1, z: coord.y }});
        setPanelVertices(this.options.vertices.positions, true);

        if (this.options.type !== Panel.types.CUSTOM){
          // make the origin the center of the triangle
          coords.forEach((coord) => { 
            coord.x -= centerX; 
            coord.y -= centerY;
          });
        }
        return generatePlanarUVMap(triangleGeometry(coords, depth));
      }

      case Panel.types.CUSTOM: {
        switch (this.options.vertices.count) {
          case 1: {
            this.options.type = Panel.types.CIRCLE;

            return this.getGeometry({
              width: this.size.width,
              height: this.size.height,
              type: this.options.type,
              depth: this.size.depth
            });
          }
          case 2: {
            throw new Error('Incorrect vertices count!');
          }
          case 3: {
            // VladP: This will override panel type in state
            //this.options.type = Panel.types.TRIANGLE;

            return generatePlanarUVMap(
              this.getGeometry({
                width: this.size.width,
                height: this.size.height,
                type: Panel.types.TRIANGLE,
                depth: this.size.depth
              })
            );
          }

          default: {
            let coords = this.options.vertices.positions;

            if (this.options.vertices.positions.length === 1) {
              this.options.type = Panel.types.CIRCLE;

              return this.getGeometry({
                width: this.size.width,
                height: this.size.height,
                type: this.options.type,
                depth: this.size.depth
              });
            } else if (
              (!this.options.vertices.count && !this.options.vertices.positions.length) ||
              this.options.vertices.positions.length === 2
            ) {
              return;
            } else if (this.options.vertices.positions.length === 3) {
              if (this.options.vertices.positions.some(i => i.z === 0 && i.y === 0)) {
                // VladP: This will override panel type in state
                //this.options.type = Panel.types.TRIANGLE;

                return generatePlanarUVMap(
                  this.getGeometry({
                    width: this.size.width,
                    height: this.size.height,
                    type: Panel.types.TRIANGLE,
                    depth: this.size.depth
                  })
                );
              }
            } else if (this.options.vertices.count) {
              if (this.options.vertices.count === 6){
                coords = this.getCircleCoords(
                  this.options.vertices.count,
                  2 * this.options.sideLength, // radius = side length; diameter = 2 * radius
                  2 * this.options.sideLength,
                  this.options.vertices.count === 8 ? 22.5 : 0);
              } else if (this.options.vertices.count === 8) {
                //Pythagorean Theorem
                let x = Math.sqrt(Math.pow(this.options.sideLength, 2) / 2);
                let diameter = 2 * x + this.options.sideLength;

                coords = this.getCircleCoords(
                  this.options.vertices.count,
                  diameter,
                  diameter,
                  22.5); //starting angle
              } else {
                coords = this.getCircleCoords(
                  this.options.vertices.count,
                  width,
                  height,
                  0
                );
              }

              setPanelVertices(coords, true);
              // VladP: update current state, in order to create the vertices labels without modifying the coordinates
              this.options.vertices.positions = coords;

            } else {
              setPanelConvexState(coords);
            }

            const shape = new THREE.Shape();

            // sort coords
            var sortedPoints = [];
            coords.forEach(coord => {
              sortedPoints.push(new THREE.Vector2(coord.z, coord.y));
            });
            sortPolygonPoints(sortedPoints);

            shape.moveTo(sortedPoints[0].x, sortedPoints[0].y);
            sortedPoints.slice(1).forEach(item => {
              shape.lineTo(item.x, item.y);
            });

            const extrudeSettings = {
              depth,
              bevelEnabled: false
            };

            return generatePlanarUVMap(new THREE.ExtrudeGeometry(shape, extrudeSettings));
          }
        }
      }

      default: {
        return generatePlanarUVMap(new THREE.BoxGeometry(width, height, depth));
      }
    }
  }

  getGeometryFromPoints(points, depth){
    if (!points || (points.length && 0 >= points.length))
      throw new Error('Cannot create the geometry with the provided points');

    // sort coords
    var sortedPoints = [];
    points.forEach(coord => {
      sortedPoints.push(new THREE.Vector2(coord.X, coord.Y));
    });
    sortPolygonPoints(sortedPoints);

    var shape = new THREE.Shape();
    shape.moveTo(sortedPoints[0].x, sortedPoints[0].y);
    for (let i = 1; i < sortedPoints.length; i++){
      shape.lineTo(sortedPoints[i].x, sortedPoints[i].y);
    }
    // shape.lineTo(sortedPoints[0].x, sortedPoints[0].y);

    const extrudeSettings = {
      depth,
      bevelEnabled: false
    };

    return generatePlanarUVMap(new THREE.ExtrudeGeometry(shape, extrudeSettings));
  }

  addFence(fenceHeight) {
    if (!fenceHeight) throw new Error('Fence height is required');

    this.fence = new Panel({
      ...this.options,
      type: this.options.type,
      width: this.size.width,
      height: this.size.height,
      depth: fenceHeight,
      color: 0xAAAAAA
    });

    this.fence.mesh.position.x = this.position.x - (this.size.depth / 2) - (this.fence.size.depth / 2);
    this.object.addObject(this.fence);
  }

  // TODO: Remove or update
  prepareMesh(type) {
    switch (type) {
      case Panel.types.CIRCLE: {
        this.panel.rotation.z = Math.PI / 2;
        break;
      }
      case Panel.types.TRIANGLE: {
        this.panel.rotation.y = Math.PI / 2;
        this.panel.position.x = -this.size.depth / 2;
        break;
      }
      case Panel.types.SQUARE:
      case Panel.types.CUSTOM: {
        this.panel.rotation.y = Math.PI / 2;
        this.panel.position.x -= this.size.depth / 2;
        break;
      }
      default: {
        break;
      }
    }
  }
/*
  generateScrews(coords, { radius, heightType, types }) {
    if (coords.length) {
      const screwLayer = new THREE.Object3D();

      for (let i = 0; i < coords.length; i++) {
        const type = types && types.find(item => (item.index === i + 1));

        const screw = new Screw({
          radius: radius,
          heightType: heightType,
          type: type && type.type,
          color: this.isObjectOnPanel(coords[i]) ? 0x000000 : 0xff0000,
          number: i + 1,
          height: parseFloat(this.size.depth) + (this.options.standOff && parseFloat(this.options.standOff.value)) || 0
        });

        coords[i].x = this.position.x - (screw.size.height / 2) + this.size.depth / 2 + Screw.minSize;

        screw.setPosition(coords[i]);

        screwLayer.add(screw.mesh);
      }

      // TODO: Hot fix
      const screwEndpoints = getEndpoints(this.screwCoordinates);
      if (this.options.dy) {
        if (this.options.dy.isCustom) {
          screwLayer.position.z = -screwEndpoints.bottom.z - this.options.dy.value
            + (this.type === Panel.types.CIRCLE ? this.size.width / 2 : this.size.height / 2);
        } else {
          // this.options.dy.value = Math.abs(screwEndpoints.bottom.z - this.size.height / 2);
        }
      }

      this.object.addMesh(screwLayer);
    }
  }

  generateScrewsForBend(coords) {
    let counter = 0;
    if (coords.length) {
      const screwLayer = new THREE.Object3D();
      coords.forEach((item, setIndex) => {
        item.forEach((position) => {
          const type = this.options.screw.types.find(item => (item.index === i + 1));

          const screw = new Screw({
            radius: this.options.screw.radius,
            heightType: this.options.screw.heightType,
            type: type && type.type,
            number: ++counter,
            height: parseFloat(this.size.depth) + Screw.minSize
          });

          if (setIndex) {
            position.x = this.position.x - (screw.size.height / 2) + this.size.depth / 2 + Screw.minSize;

            screw.mesh.rotation.y = Math.PI / 2;
          } else {
            position.z += this.position.z + this.size.topHeight / 2;
            position.x += (this.position.x + this.size.height / 2) - (screw.size.height / 2) + Screw.minSize;
          }

          screw.setPosition(position);

          // VladP: Translate position with dy only for side screws
          if (setIndex === 0) // top screws
            this.object.addObject(screw);
          else // index === 1 side screws
            screwLayer.add(screw.mesh);
        });
      });

      const screwEndpoints = getEndpoints(coords, true);
      if (this.options.dy) {
        if (this.options.dy.isCustom) {
          screwLayer.position.x = (screwEndpoints.bottom.z - this.size.height / 2 + this.options.dy.value);
          // screwLayer.position.z = -this.options.lyh.value / 2;
        } else {
        // this.options.dy.value = Math.abs(screwEndpoints.bottom.z - this.size.height / 2);
        }
      }

      this.object.addMesh(screwLayer);
    }
  }

  isObjectOnPanel(targetPosition) {
    const halfWidth = this.size.width / 2;
    const screwRadius = this.options.screw.radius;

    switch (this.options.type) {
      case Panel.types.CIRCLE: {
        let dx = targetPosition.y - this.position.y;
        let dy = targetPosition.z - this.position.z;
        let radii = halfWidth; // + screwRadius

        return (dx * dx) + (dy * dy) < radii * radii
      }
      default: {
        const parent = {
          left: this.position.y + halfWidth,
          right: this.position.y - halfWidth,
          top: this.position.y + halfWidth,
          bottom: this.position.y - halfWidth
        };
        const halfScrewRadius = screwRadius / 2;

        return (targetPosition.y - halfScrewRadius < parent.left && targetPosition.y + halfScrewRadius > parent.right) &&
          (targetPosition.z - halfScrewRadius < parent.top && targetPosition.z + halfScrewRadius > parent.bottom);
      }
    }
  }

  createDyLabel() {
    const { bottom: bottomScrewPosition, left: leftScrewPosition } = getEndpoints(this.screwCoordinates, this.options.type === Panel.types.BEND);
    const padding = LabelsPadding;

    const position = {
      x: 0,
      y: leftScrewPosition.y + padding,
      z: this.options.type === Panel.types.CIRCLE ? this.size.width / 2 : this.size.height / 2
    };

    const arrow = new Arrow({
      position,
      size: -this.options.dy.value,
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });
    if (this.options.type === Panel.types.BEND) {
      arrow.mesh.rotation.y = Math.PI / -2;
      arrow.mesh.rotation.x = Math.PI;
    }
    if (this.options.dy.value)
      this.object.addObject(arrow);

    const htmlElement = new HTMLEditableLabel({
      text: `Dy = ${unitTransform(+this.options.dy.value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.dy.value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER
    });
    htmlElement.onInputChange((value) => {
      this.dispatchOptionUpdate({
        panel: {
          dy: {
            value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE),
            isCustom: true
          }
        }
      });
    });

    return {
      arrow,
      htmlElement
    };
  }
*/
  createWidthLabel(options) {
    const object = new THREE.Object3D();
    const padding = 35;//this.size.height / 6;

    const widthArrow = new Arrow({
      position: {
        x: this.panel.position.x + (this.type === Panel.types.CIRCLE ? 0 : this.size.depth / 2),
        y: this.panel.position.y - this.size.width / 2,
        z: this.panel.position.z - (this.type === Panel.types.CIRCLE ? this.size.width / 2 : this.size.height / 2) - padding
      },
      size: this.size.width,
      direction: Arrow.types.direction.Y,
      disableDepthTest: true
    });
    object.add(widthArrow.mesh);

    const rightLine = new Line({
      from: {
        x: this.panel.position.x + (this.type === Panel.types.CIRCLE ? 0 : this.size.depth / 2),
        y: this.panel.position.y - this.size.width / 2,
        z: this.panel.position.z - (this.type === Panel.types.CIRCLE ? 0 : this.size.height / 2)
      },
      to: {
        x: this.panel.position.x + (this.type === Panel.types.CIRCLE ? 0 : this.size.depth / 2),
        y: this.panel.position.y - this.size.width / 2,
        z: this.panel.position.z - (this.type === Panel.types.CIRCLE ? this.size.width / 2 : this.size.height / 2) - padding
      },
      disableDepthTest: true
    });
    object.add(rightLine.mesh);

    const leftLine = new Line({
      from: {
        x: this.panel.position.x + (this.type === Panel.types.CIRCLE ? 0 : this.size.depth / 2),
        y: this.panel.position.y + this.size.width / 2,
        z: this.panel.position.z - (this.type === Panel.types.CIRCLE ? 0 : this.size.height / 2)
      },
      to: {
        x: this.panel.position.x + (this.type === Panel.types.CIRCLE ? 0 : this.size.depth / 2),
        y: this.panel.position.y + this.size.width / 2,
        z: this.panel.position.z - (this.type === Panel.types.CIRCLE ? this.size.width / 2 : this.size.height / 2) - padding
      },
      disableDepthTest: true
    });
    object.add(leftLine.mesh);

    if (this.options.type === Panel.types.BEND) {
      object.position.z += this.size.depth / 2;
      object.rotation.y = Math.PI / -2;
      object.rotation.x = Math.PI;
    }

    // this.object.addMesh(object);

    let text = ((!options || !options.is3DMode || options.showLabels) ? `Lx = ` : "") +
              `${unitTransform(this.size.width || 0).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;

    const htmlElement = new HTMLEditableLabel({
      text,
      value: unitTransform(this.size.width || 0).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          width: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      this.panel.position.x + (this.type === Panel.types.CIRCLE ? 0 : this.size.depth / 2),
      this.panel.position.y,
      this.panel.position.z - (this.type === Panel.types.CIRCLE ? this.size.width / 2 : this.size.height / 2) - padding
    );

    if (options && options.is3DMode) {
      if (!options.displayHorizontally && options.rotation) {
        let startPos = new THREE.Vector3(widthArrow.position.x, widthArrow.position.y, widthArrow.position.z);
        startPos.applyEuler(options.rotation);
        let endPos = new THREE.Vector3(widthArrow.endPosition.x, widthArrow.endPosition.y, widthArrow.endPosition.z);
        endPos.applyEuler(options.rotation);
        htmlElement.setDirection(startPos, endPos, false);
      }
      
      htmlElement.position.z -= 20; // add padding
      htmlElement.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      htmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      htmlElement.align = HTMLEditableLabel.types.align.CENTER;
    }

    return {
      line: object,
      htmlElement,
      padding
    };
  }

  createHeightLabel(options) {
    const object = new THREE.Object3D();
    const padding = 35;//this.size.width / 6;

    const heightArrow = new Arrow({
      position: {
        x: this.panel.position.x + this.size.depth / 2,
        y: this.panel.position.y - this.size.width / 2 - padding,
        z: this.panel.position.z - this.size.height / 2
      },
      size: this.size.height,
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });
    object.add(heightArrow.mesh);

    const topLine = new Line({
      from: {
        x: this.panel.position.x + this.size.depth / 2,
        y: this.panel.position.y - this.size.width / 2,
        z: this.panel.position.z - this.size.height / 2
      },
      to: {
        x: this.panel.position.x + this.size.depth / 2,
        y: this.panel.position.y - this.size.width / 2 - padding,
        z: this.panel.position.z - this.size.height / 2
      },
      disableDepthTest: true
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: this.panel.position.x + this.size.depth / 2,
        y: this.panel.position.y - this.size.width / 2,
        z: this.panel.position.z + this.size.height / 2
      },
      to: {
        x: this.panel.position.x + this.size.depth / 2,
        y: this.panel.position.y - this.size.width / 2 - padding,
        z: this.panel.position.z + this.size.height / 2
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);

    if (this.options.type === Panel.types.BEND) {
      object.position.z += this.size.depth / 2;
      object.rotation.y = Math.PI / -2;
      object.rotation.x = Math.PI;
    }

    // this.object.addMesh(object);

    let text = ((!options || !options.is3DMode || options.showLabels) ? `Ly = ` : "") +
              `${unitTransform(this.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const htmlElement = new HTMLEditableLabel({
      text,
      value: unitTransform(this.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          height: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      this.panel.position.x + this.size.width / 2 + padding + this.size.depth / 2,
      this.panel.position.y,
      this.panel.position.z 
    );
    
    if (options && options.is3DMode && !options.isSafetyBarrier) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(heightArrow.position.x, heightArrow.position.y, heightArrow.position.z);
        startPos.applyEuler(options.rotation);
        var endPos = new THREE.Vector3(heightArrow.endPosition.x, heightArrow.endPosition.y, heightArrow.endPosition.z);
        endPos.applyEuler(options.rotation);
        htmlElement.setDirection(startPos, endPos, false);
      }

      htmlElement.position.x += 20; // add padding
      htmlElement.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      htmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      htmlElement.align = HTMLEditableLabel.types.align.CENTER;
    }

    return {
      line: object,
      padding,
      htmlElement
    };
  }

  createDepthLabel() {
    let position = {};
    if (this.options.type === Panel.types.CIRCLE){
      //x = cx + r * cos(a)
      //y = cy + r * sin(a)
      position = {
        x: this.panel.position.x + this.size.depth,
        y: this.panel.position.y - this.size.width / 2 * Math.cos(45 * Math.PI / 180) + 5,
        z: this.panel.position.z - this.size.width / 2 * Math.sin(45 * Math.PI / 180) + 5
      };
    } else if (this.options.type === Panel.types.TRIANGLE){
      position = {
        x: this.panel.position.x + this.size.depth,
        y: this.panel.position.y,
        z: this.panel.position.z - this.size.height / 2 + LabelsPadding
      };
    } else if (this.options.type === Panel.types.CUSTOM){
      const endpoints = getEndpoints(this.options.vertices.positions);
      position = {
        x: this.panel.position.x + this.size.depth,
        y: endpoints.right.y + LabelsPadding, 
        z: -endpoints.right.z + (endpoints.right.z < 0 ? -LabelsPadding : LabelsPadding) // add smaller padding to be near the anchor
      };
    } else {
      position = {
        x: this.panel.position.x + this.size.depth,
        y: this.panel.position.y - this.size.width / 2 + LabelsPadding / 2,
        z: this.panel.position.z - this.size.height / 2 + LabelsPadding
      };
    }
    const path = [{ ...position }];

    position.y -= 100;//this.size.width / 3;
    position.z -= 100;//this.size.height / 3;
    path.push({ ...position });

    position.y -= 100;
    path.push({ ...position });

    const line = new Line({ path, disableDepthTest: true });


    if (this.options.type === Panel.types.BEND) {
      line.mesh.rotation.y = Math.PI / -2;
      line.mesh.rotation.x = Math.PI;
    }

    this.object.addObject(line);
    const depthValue = Scene.units.distance === DISTANCE.INCHES ?
      prepareNum(unitTransform(this.size.depth || 0, false).from(defaultUnits.DISTANCE).to(Scene.units.distance), false, 3) :
      unitTransform(this.size.depth || 0, true).from(defaultUnits.DISTANCE).to(Scene.units.distance)

    const htmlElement = new HTMLEditableLabel({
      text: `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = ${depthValue}`,
      value: depthValue,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM,
      minValue: 0
    });
    htmlElement.position.set(
      -path[1].y,
      path[1].x,
      path[1].z
    );

    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });

    return {
      line,
      htmlElement,
      path
    };
  }

  create3DDepthLabel(options) {
    let position = {};
    if (this.options.type === Panel.types.CIRCLE){
      //x = cx + r * cos(a)
      //y = cy + r * sin(a)
      position = {
        x: this.panel.position.x - this.size.depth / 2,
        y: this.panel.position.y - this.size.width / 2 * Math.cos(45 * Math.PI / 180),
        z: this.panel.position.z - this.size.width / 2 * Math.sin(45 * Math.PI / 180)
      };
    } else if (this.options.type === Panel.types.TRIANGLE){
      position = {
        x: this.panel.position.x,
        y: this.panel.position.y,
        z: this.panel.position.z - this.size.height / 2 // + LabelsPadding
      };
    } else if (this.options.type === Panel.types.CUSTOM){
      const endpoints = getEndpoints(this.options.vertices.positions);
      position = {
        x: this.panel.position.x,
        y: endpoints.right.y, // + LabelsPadding, 
        z: -endpoints.right.z // + (endpoints.right.z < 0 ? -LabelsPadding : LabelsPadding) // add smaller padding to be near the anchor
      };
    } else {
      position = {
        x: this.panel.position.x,
        y: this.panel.position.y - this.size.width / 2, // + LabelsPadding / 2,
        z: this.panel.position.z - this.size.height / 2 // + LabelsPadding
      };
    }
    
    let top = {
      from: {
        x: position.x + this.size.depth,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x + this.size.depth,
        y: Math.min(position.y, -this.size.width / 2) - LabelsPadding, // width might not be well defined for custom plate
        z: position.z - LabelsPadding
      }
    };

    let bottom = {
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: Math.min(position.y, -this.size.width / 2) - LabelsPadding, // width might not be well defined for custom plate
        z: position.z - LabelsPadding
      }
    };

    const depthValue = Scene.units.distance === DISTANCE.INCHES ?
      prepareNum(unitTransform(this.size.depth || 0, false).from(defaultUnits.DISTANCE).to(Scene.units.distance), false, 3) :
      unitTransform(this.size.depth || 0, true).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    let depthText = ((!options || !options.is3DMode || options.showLabels) ? `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = ` : "") +
                    `${depthValue}`;

    const depth = addDistanceLabel({
      top,
      bottom,
      size: -this.size.depth, // to reverse the top / bottom coords
      arrowDirection: Arrow.types.direction.X,
      labelText: depthText,
      labelValue: depthValue,
      align: HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      }
    });

    depth.text.position.x = position.z - LabelsPadding; // - Math.min(groundPadding.bottom, this.size.height / 2 + 100); 
    depth.text.position.y = this.position.x;
    depth.text.position.z = Math.min(position.y, -this.size.width / 2) - LabelsPadding; // - Math.min(groundPadding.left, this.size.width / 2 + 100);

    // if (rotation) {
      // var startPos = new THREE.Vector3(bottom.to.x, bottom.to.y, bottom.to.z);
      // startPos.applyEuler(rotation);
      // var endPos = new THREE.Vector3(top.to.x, top.to.y, top.to.z);
      // endPos.applyEuler(rotation);
      // depth.text.setDirection(startPos, endPos, true);
      // depth.text.position.x -= 20; // add padding
      // depth.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      // depth.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      // depth.text.align = HTMLEditableLabel.types.align.CENTER;
    // }

    // the dimension will move while the panel is animated
    // this.object.addMesh(depth.object);

    return {
      object: depth.object,
      htmlElement: depth.text
    };
  }

  create3DDepthLabelForBend(options) {
    if (this.options.type !== Panel.types.BEND)
      return;

    let position = new THREE.Vector3();
    this.panel.children[0].getWorldPosition(position);
    position.x -= this.size.depth / 2;
    position.y += this.size.height / 2;
    position.z -= this.size.width / 2;

    let top = {
      from: {
        x: position.x + this.size.depth,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x + this.size.depth,
        y: position.y + LabelsPadding,
        z: position.z - LabelsPadding
      }
    };

    let bottom = {
      from: {
        x: position.x,
        y: position.y,
        z: position.z
      },
      to: {
        x: position.x,
        y: position.y + LabelsPadding, 
        z: position.z - LabelsPadding
      }
    };

    const depthValue = Scene.units.distance === DISTANCE.INCHES ?
      prepareNum(unitTransform(this.size.depth || 0, false).from(defaultUnits.DISTANCE).to(Scene.units.distance), false, 3) :
      unitTransform(this.size.depth || 0, true).from(defaultUnits.DISTANCE).to(Scene.units.distance);
    let depthText = ((!options || !options.is3DMode || options.showLabels) ? `${APP_TEXTS.BASE_PLATE_THICKNESS_LABEL.Content} = ` : "") +
                    `${depthValue}`;

    const depth = addDistanceLabel({
      top,
      bottom,
      size: -this.size.depth, // to reverse the top / bottom coords
      arrowDirection: Arrow.types.direction.X,
      labelText: depthText,
      labelValue: depthValue,
      align: HTMLEditableLabel.types.align.LEFT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          panel: {
            depth: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      }
    });

    // depth.text.position.x = position.z - LabelsPadding; // - Math.min(groundPadding.bottom, this.size.height / 2 + 100); 
    depth.text.position.x = -depth.text.position.x;
    depth.text.position.y = position.y + LabelsPadding;
    depth.text.position.z = position.z - LabelsPadding; // - Math.min(groundPadding.left, this.size.width / 2 + 100);

    return {
      object: depth.object,
      htmlElement: depth.text
    };
  }

  createLyhLabel(options) {
    if (this.panel.children.length !== 2)
      return;

    const object = new THREE.Object3D();
    let topPanelPosition = new THREE.Vector3();
    this.panel.children[1].getWorldPosition(topPanelPosition);
    const heightArrow = new Arrow({
      position: {
        x: topPanelPosition.x - this.size.topHeight / 2 + this.size.depth / 2,
        y: topPanelPosition.y + this.size.depth / 2,
        z: topPanelPosition.z + this.size.width / 2 + LabelsPadding
      },
      size: this.size.topHeight,
      direction: Arrow.types.direction.X,
      disableDepthTest: true
    });
    object.add(heightArrow.mesh);

    const topLine = new Line({
      from: {
        x: heightArrow.position.x,
        y: heightArrow.position.y,
        z: heightArrow.position.z
      },
      to: {
        x: heightArrow.position.x,
        y: heightArrow.position.y,
        z: heightArrow.position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(topLine.mesh);

    const bottomLine = new Line({
      from: {
        x: heightArrow.position.x + this.size.topHeight,
        y: heightArrow.position.y,
        z: heightArrow.position.z
      },
      to: {
        x: heightArrow.position.x + this.size.topHeight,
        y: heightArrow.position.y,
        z: heightArrow.position.z - LabelsPadding
      },
      disableDepthTest: true
    });
    object.add(bottomLine.mesh);

    let text = ((!options || options.showLabels) ? `Lyh = ` : "") +
              `${unitTransform(this.size.topHeight).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const htmlElement = new HTMLEditableLabel({
      text,
      value: unitTransform(this.size.topHeight).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          lyh: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      heightArrow.position.x + this.size.topHeight / 2,
      heightArrow.position.y,
      heightArrow.position.z 
    );
    
    htmlElement.position.z += 20; // padding
    if (!options.displayHorizontally) {
      htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      htmlElement.setDirection(
        new THREE.Vector3(heightArrow.position.x, heightArrow.position.y, heightArrow.position.z),
        new THREE.Vector3(heightArrow.endPosition.x, heightArrow.endPosition.y, heightArrow.endPosition.z),
        false
      );
    } else {
      htmlElement.align = HTMLEditableLabel.types.align.CENTER;
      // htmlElement.addDynamicPadding(HTMLLabel.types.axis.Z, HTMLLabel.types.dimension.WIDTH, HTMLLabel.types.padding.ADDHALF);
    }

    return {
      object,
      htmlElement
    };
  }

  // isFixed: is not relative to screw position (used for general calculation)
  createS1Label(screwGroup, isFixed, padding, startZ, options) {
    const lastIndex = screwGroup.screwCoordinates.length - 1;
    // let first = screwGroup.screwCoordinates[lastIndex];
    // let second = screwGroup.screwCoordinates[lastIndex - screwGroup.options.count.m] || screwGroup.screwCoordinates[0];
    let first;
    let second;
    screwGroup.screwCoordinates.forEach((coord) => {
      if (!first || coord.y > first.y) { // because everything is upside down
        first = coord;
      }
    });
    screwGroup.screwCoordinates.forEach((coord) => {
      if (!second || coord.y === first.y - Number(screwGroup.options.padding.s1)) {
        second = coord;
      }
    });
    if (!first || !second) return {};

    // take the coordinates of the bottom screws
    screwGroup.screwCoordinates.forEach((coord) => {
      if (coord.y === first.y && coord.z > first.z)
        first = coord;
      else if (coord.y === second.y && coord.z > second.z)
        second = coord;
    });

    let isBendPanelTop = false;
    let xCoord = this.size.depth / 2;
    if (this.options.type === Panel.types.BEND) {
      if (screwGroup.screwCoordinates[1].length > 1) {
        isBendPanelTop = false;
        first = screwGroup.screwCoordinates[1][0];
        second = screwGroup.screwCoordinates[1][1];
        xCoord *= -1;
      } else if (screwGroup.screwCoordinates[0].length > 1) {
        first = screwGroup.screwCoordinates[0][0];
        second = screwGroup.screwCoordinates[0][1];
        // xCoord = - this.size.topHeight / 2 + screwGroup.screws[0].options.radius / 2;
        xCoord = - first.z;

        isBendPanelTop = true;
      } else {
        return {};
      }
    }

    if (!first || !second) return {};

    // const padding = this.size.width / 4;

    const top = {
      from: {
        x: xCoord,
        y: first.y,
        z: isFixed ? second.z : startZ
      },
      to: {
        x: xCoord,
        y: first.y,
        z: padding + (isFixed ? 0 : startZ)
      }
    };

    const bottom = {
      from: {
        x: xCoord,
        y: second.y,
        z: isFixed ? second.z : startZ
      },
      to: {
        x: xCoord,
        y: second.y,
        z: padding + (isFixed ? 0 : startZ)
      }
    };

    const size = bottom.to.y - top.to.y;

    let text = ((!options || !options.is3DMode || options.showLabels) ? `S1 = ` : "") +
              `${unitTransform(screwGroup.options.padding.s1).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const S1 = addDistanceLabel({
      top,
      bottom,
      size,
      arrowDirection: Arrow.types.direction.Y,
      labelText: text,
      labelValue: unitTransform(screwGroup.options.padding.s1).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          screw: {
            padding: {
              s1: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });

    if (this.options.type === Panel.types.BEND) {
      if (isBendPanelTop) {
        S1.object.rotation.y = Math.PI / 2;
        S1.text.position.z = -S1.text.position.z;
        S1.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.BOTTOM
      } else {
        S1.object.rotation.y = Math.PI / -2;
      }
    }

    if (options && options.is3DMode && !options.isSafetyBarrier) {
      if (!options.displayHorizontally && options.rotation) {
        var startPos = new THREE.Vector3(bottom.to.x, bottom.to.y, bottom.to.z)
        // startPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
        // startPos.applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2);
        // convert from 2D coordinates to default 3D coordinates
        // startPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), -starMath.PI / 2);
        // convert to 3D rotation
        startPos.applyEuler(options.rotation);
        // startPos.applyAxisAngle(new THREE.Vector3(1, 0, 0), initialRotation.x);
        // startPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), initialRotation.y);
        // startPos.applyAxisAngle(new THREE.Vector3(0, 0, 1), initialRotation.z);
        var endPos = new THREE.Vector3(top.to.x, top.to.y, top.to.z)
        // endPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
        // endPos.applyAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2);
        // convert from 2D coordinates to default 3D coordinates
        // endPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI / 2);
        // convert to 3D rotation
        endPos.applyEuler(options.rotation);
        // endPos.applyAxisAngle(new THREE.Vector3(1, 0, 0), initialRotation.x);
        // endPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), initialRotation.y);
        // endPos.applyAxisAngle(new THREE.Vector3(0, 0, 1), initialRotation.z);
        S1.text.setDirection(startPos, endPos, false);
      }
      // only for general/bracket, for safety barriers change position on each type
      S1.text.position.z += 20; // add forward padding
      S1.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      S1.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
    }

    return {
      object: S1.object,
      htmlElement: S1.text || null
    };
  }

  // isFixed: is not relative to screw position (used for general calculation)
  createS2Label(screwGroup, isFixed, padding, startY, options) {
    let first;
    let second;

    screwGroup.screwCoordinates.forEach((coord) => {
      if (!first || coord.z > first.z) {
        if (!second || coord.z > second.z) {
          first = second;
          second = coord;
        } else if (coord.z < second.z){
          first = coord;
        }
      }
    });

    if (!first || !second) return {};

    // take the coordinates of the most left screw
    screwGroup.screwCoordinates.forEach((coord) => {
      if (coord.z === first.z && coord.y > first.y)
        first = coord;
      else if (coord.z === second.z && coord.y > second.y)
        second = coord;
    });

    
    const top = {
      from: {
        x: this.size.depth / 2,
        y: first.y,
        z: isFixed ? first.z : startY
      },
      to: {
        x: this.size.depth / 2,
        y: isFixed ? padding : first.y + padding,
        z: isFixed ? first.z : startY
      }
    };

    const bottom = {
      from: {
        x: this.size.depth / 2,
        y: first.y,
        z: isFixed ? second.z : startY - Math.abs(second.z - first.z)
      },
      to: {
        x: this.size.depth / 2,
        y: isFixed ? padding : first.y + padding,
        z: isFixed ? second.z : startY - Math.abs(second.z - first.z)
      }
    };

    let text = ((!options || !options.is3DMode || options.showLabels) ? `S2 = ` : "") +
              `${unitTransform(screwGroup.options.padding.s2).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`;
    const S2 = addDistanceLabel({
      top,
      bottom,
      size: Math.abs(second.z - first.z),
      arrowDirection: Arrow.types.direction.Z,
      labelText: text,
      labelValue: unitTransform(screwGroup.options.padding.s2).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.RIGHT,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          screw: {
            padding: {
              s2: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });

    if (options && options.is3DMode && !options.isSafetyBarrier) {
        if (!options.displayHorizontally && options.rotation) {
          var startPos = new THREE.Vector3(bottom.to.x, bottom.to.y, bottom.to.z);
          startPos.applyEuler(options.rotation);
          var endPos = new THREE.Vector3(top.to.x, top.to.y, top.to.z);
          endPos.applyEuler(options.rotation);
          S2.text.setDirection(startPos, endPos, false);
        }
        // only for general/bracket, for safety barriers change position on each type
        S2.text.position.x -= 20; // add padding
        S2.text.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
        S2.text.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
        S2.text.align = HTMLEditableLabel.types.align.CENTER;
    }

    // this.object.addMesh(S2.object);

    return {
      object: S2.object,
      htmlElement: S2.text
    };
  }

  getCircleCoords(count, width, height, correction = 0, isInner = false) {
    const coordinates = [];

    for (let i = 0; i < count; i++) {
      const position = new THREE.Vector3();

      if (count > 1) {
        if (count === 4) {
          correction = 45;
        }

        const angle = i * toRadians(360 / count) + toRadians(correction);

        if (count === 4 && !isInner) {
          position.y += prepareNum((width / 2) * Math.sqrt(2) * Math.cos(angle));
          position.z += prepareNum((height / 2) * Math.sqrt(2) * Math.sin(angle));
        } else {
          position.y += prepareNum((width / 2) * Math.cos(angle));
          position.z += prepareNum((height / 2) * Math.sin(angle));
        }
      }

      coordinates.push(position);
    }

    return coordinates;
  }

  updatePosition() {
    switch (this.options.position) {
      case Panel.positions.VERTICAL_X: {
        this.mesh.rotation.x = Math.PI / 2;
        this.mesh.rotation.y = 0;
        this.mesh.rotation.z = 0;

        if (this.options.type === Panel.types.BEND) {
          this.mesh.rotation.x = Math.PI / -2;
          this.mesh.rotation.y = Math.PI / -2;
          this.mesh.rotation.z = 0;
        }

        break;
      }

      case Panel.positions.VERTICAL_X_INVERT: {
        this.mesh.rotation.x = Math.PI / 2;
        this.mesh.rotation.y = 0;
        this.mesh.rotation.z = Math.PI;

        if (this.options.type === Panel.types.BEND) {
          this.mesh.rotation.x = Math.PI / 2;
          this.mesh.rotation.y = Math.PI / 2;
          this.mesh.rotation.z = 0;
        }

        break;
      }

      case Panel.positions.VERTICAL_Y: {
        this.mesh.rotation.x = Math.PI / 2;
        this.mesh.rotation.y = 0;
        this.mesh.rotation.z = Math.PI / 2;

        break;
      }

      case Panel.positions.VERTICAL_Y_INVERT: {
        this.mesh.rotation.x = Math.PI / 2;
        this.mesh.rotation.y = 0;
        this.mesh.rotation.z = Math.PI / -2;

        break;
      }

      case Panel.positions.HORIZONTAL_X: {
        this.mesh.rotation.x = Math.PI / 2;
        this.mesh.rotation.y = Math.PI / 2;
        this.mesh.rotation.z = 0;

        break;
      }

      case Panel.positions.HORIZONTAL_Y: {
        this.mesh.rotation.x = Math.PI;
        this.mesh.rotation.y = 0;
        this.mesh.rotation.z = Math.PI / -2;

        break;
      }

      default: {
        break;
      }
    }
  }

  getCompressionLine(){
    var line;
    if (this.options.compressionOptions && this.options.compressionOptions.hasCompressionZone){
      line = new Line({
        from: {
          x: this.panel.position.z,
          y: -this.options.compressionOptions.compressionLine.P1.x,
          z: -this.options.compressionOptions.compressionLine.P1.y
        },
        to: {
          x: this.panel.position.z,
          y: -this.options.compressionOptions.compressionLine.P2.x,
          z: -this.options.compressionOptions.compressionLine.P2.y
        },
        disableDepthTest: true,
        dashed: true,
        customMaterial: new THREE.LineDashedMaterial({
          color: 0xFF0000,
          linewidth: 10, // Due to limitations of the OpenGL Core Profile with the WebGL renderer on most platforms linewidth will always be 1 regardless of the set value.
          scale: 1,
          dashSize: 10,
          gapSize: 5
        })
      });
    }
    return {
      compressionLine: line
    };
  }

  getVerticesLabels(allowDrag){
    let vertices;
    if (allowDrag)
      vertices = [];
    else 
      vertices = new AbstractObject();
    if (0 < this.options.vertices.positions.length) {
      this.options.vertices.positions.forEach((pos, index) => {
        const { label, mesh } = this.getVertexLabel(index + 1, pos.y * -1, pos.z * -1);
        if (allowDrag) {
          vertices.push(new DraggableObject({ mesh, htmlObject: label }));
        } else {
          vertices.htmlLayer.add(label);
          vertices.object.addMesh(mesh);
        }
      });
    }

    return vertices;
  }

  getVertexLabel(index, posX, posY) {
    const size = 5;

    const shape = new THREE.Shape();
    shape.moveTo(posX - size, posY - size);
    shape.lineTo(posX + size, posY - size);
    shape.lineTo(posX + size, posY + size);
    shape.lineTo(posX - size, posY + size);
    shape.lineTo(posX - size, posY - size);

    const extrudeSettings = {
      depth: 1,
      bevelEnabled: false
    };
    var geometry = new THREE.ExtrudeBufferGeometry(shape, extrudeSettings);
    var material = new THREE.MeshLambertMaterial({ color: 0xff0000 });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.y = -Math.PI / 2;
    mesh.rotation.x = Math.PI / 2;

    mesh.position.x += this.options.depth; // plate thickness

    const label = new HTMLLabel({
      text: index,
      color: "#ffffff",
      bold: true,
      verticalAlign: HTMLLabel.types.verticalAlign.CENTER,
      align: HTMLLabel.types.align.CENTER
    });
    label.position.set(
      posX,
      0,
      posY,
    );

    return {
      label,
      mesh
    }
  }

  createZonesLabels() {
    if (this.type !== Panel.types.BEND && this.panel.children.length > 1) {
      let zonesLayer = new Layer();
      for (let i = 0; i < this.panel.children.length; i++) {
        let child = this.panel.children[i];
        let points = [];
        for (let j = 0; j < child.geometry.attributes.position.count; j++) {
          points.push({ 
              x: child.geometry.attributes.position.getX(j), 
              y: child.geometry.attributes.position.getY(j), 
              z: child.geometry.attributes.position.getZ(j)
          });
        }
        let center = getCenterOfPoints(points);
        center.applyEuler(this.panel.rotation);
        center.x += this.panel.position.x;
        center.y += this.panel.position.y;
        center.z += this.panel.position.z;

        let path = [{ ...center }];
        center = (new THREE.Vector3(0, -100, -100).add(center));
        path.push({ ...center });
        center = (new THREE.Vector3(0, -100, 0).add(center));
        path.push({ ...center });

        let line = new Line({ path, disableDepthTest: true });
        zonesLayer.addObject(line);
        let text = child.name === "TensionZone" ? APP_TEXTS.TENSION_ZONE.Content : APP_TEXTS.COMPRESSION_ZONE.Content;
        let htmlElement = new HTMLLabel({
          text: text,
          verticalAlign: HTMLLabel.types.verticalAlign.BOTTOM,
          align: HTMLEditableLabel.types.align.CENTER,
        });

        let htmlPos = new THREE.Vector3(-path[1].y + 50, path[1].x, path[1].z);
        htmlElement.position.set(
          htmlPos.x,
          htmlPos.y,
          htmlPos.z
        );

        // htmlElement.setDirection(new THREE.Vector3(path[1].x, path[1].y, path[1].z).applyEuler(parentRot), 
        //   new THREE.Vector3(path[2].x, path[2].y, path[2].z).applyEuler(parentRot));
        zonesLayer.htmlLayer.add(htmlElement);
      }

      return zonesLayer;
    }
  }

}

export function setPanelVertices(coords, ignoreObjectUpdate) {
  var sortedCoords = coords.map(i => ({ x: i.y, y: i.z }));
  sortPolygonPoints(sortedCoords);
  Scene.onUpdate(
    {
      panel: {
        vertices: {
          positions: coords,
          isConvex: isConvex(sortedCoords)
        }
      }
    },
    ignoreObjectUpdate
  );

  // VladP: Use a single update for both positions & convex state to improve performance
  // setPanelConvexState(coords);
}

export function setPanelVerticesCount(count, ignoreObjectUpdate) {
  // 0 - use the panel.vertices.positions to generate the plate
  // != 0 - generates the coordinates and ignores the panel.vertices.positions
  Scene.onUpdate(
    {
      panel: {
        vertices: {
          count
        }
      }
    },
    ignoreObjectUpdate
  );
}
export function setPanelType(type, ignoreObjectUpdate) {
  Scene.onUpdate(
    {
    panel: {
      type
    }
    },
    ignoreObjectUpdate
  );
}

function setPanelConvexState(coords) {
  var sortedCoords = coords.map(i => ({ x: i.y, y: i.z }));
  sortPolygonPoints(sortedCoords);
  Scene.onUpdate(
    {
      panel: {
        vertices: {
          isConvex: isConvex(sortedCoords)
        }
      }
    },
    true
  );
}



// WEBPACK FOOTER //
// ./src/object-view/objects/panel.js